import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useRef } from "react";
import { useBreadcrumb } from "./useBreadcrumb";
import { useRecentPages } from "./useRecentPages";

export interface TrailEntry { path?: string; label: string; }

/**
 * Powers the two-layer navigation affordance:
 *
 *   Layer 1 - Hierarchical breadcrumb (always):
 *     Derived from the menu tree, fresh every render - never a separately
 *     tracked mutable ref. Shows where the current page sits in the
 *     navigation structure, e.g. "Settings > AI Provider". A parent crumb
 *     with no genuine distinct target (e.g. Deployment, whose only page IS
 *     the current one) has no `path` and renders as plain text rather than
 *     a same-URL dead link. See ds/Breadcrumb.tsx - crumbs are plain text/
 *     links with no back-arrow affix; there is no separate "quick back"
 *     widget with its own state that can drift out of sync with the trail
 *     (that widget, `crossSectionBack`, was removed 2026-07 after it was
 *     found to freeze across same-section navigations - see postmortems.md).
 *
 *   Layer 2 - Recently visited (opt-in, via the clock icon):
 *     A running list of the last 8 distinct pages visited, persisted to
 *     localStorage. Never rendered inline in the breadcrumb - surfaced only
 *     through the history popover so the hierarchical trail stays clean.
 */
export function useNavigationTrail() {
  const location = useLocation();
  const navigate = useNavigate();
  const menuBreadcrumb = useBreadcrumb();
  const { recentPages, record: recordRecentPage, clear: clearRecentPages } = useRecentPages();

  // Prevents double-firing on StrictMode double-invoke.
  const lastProcessedPathRef = useRef<string | null>(null);

  const friendlyLabel = useMemo(() => {
    const last = menuBreadcrumb[menuBreadcrumb.length - 1];
    if (last?.label) return last.label;
    const seg = location.pathname.split("/").filter(Boolean).pop() ?? "";
    return (
      seg
        .split(/[-_]/)
        .filter(Boolean)
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ") || "Home"
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, menuBreadcrumb]);

  // trail - the hierarchical menu-based breadcrumbs, repackaged as
  // TrailEntry[] for backwards-compat with callers that read trail.length /
  // trail[i].label. `path` is left undefined (rather than falling back to
  // the current page's own path) whenever the menu config has no genuine
  // distinct target for that crumb - the renderer treats that as plain,
  // non-clickable text instead of a dead same-URL link.
  const trail: TrailEntry[] = menuBreadcrumb.map(c => ({
    path: c.to,
    label: c.label,
  }));

  useEffect(() => {
    const path = location.pathname;
    if (lastProcessedPathRef.current === path) return;
    lastProcessedPathRef.current = path;
    recordRecentPage(path, friendlyLabel, menuBreadcrumb[0]?.label ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, friendlyLabel]);

  function goBack() {
    const parent = trail[trail.length - 2];
    if (parent?.path) {
      navigate(parent.path);
      return;
    }
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/");
  }

  return {
    trail,
    breadcrumbs: menuBreadcrumb,
    goBack,
    current: trail[trail.length - 1] ?? { path: location.pathname, label: friendlyLabel },
    recentPages,
    clearRecentPages,
  };
}
